import { doc, onSnapshot, setDoc } from "firebase/firestore";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useAuth } from "./auth";
import { db } from "./firebase";

/**
 * Returns today's date in YYYY-MM-DD using the user's local time zone.
 * (toISOString() would give UTC and roll over at midnight UTC — wrong for us.)
 */
function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Apply a React-style setter argument (value or updater fn) to a previous value.
 */
function resolveSetter<T>(prev: T, next: SetStateAction<T>): T {
  return typeof next === "function" ? (next as (p: T) => T)(prev) : next;
}

export type CheckedMap = Record<string, boolean>;
export type LastDoneMap = Record<string, string>;

export interface ChecklistState {
  loading: boolean;
  /** Today's task checkmarks. Date-scoped, so resets each calendar day. */
  checked: CheckedMap;
  setChecked: Dispatch<SetStateAction<CheckedMap>>;
  checkedMonthly: CheckedMap;
  setCheckedMonthly: Dispatch<SetStateAction<CheckedMap>>;
  checkedSick: CheckedMap;
  setCheckedSick: Dispatch<SetStateAction<CheckedMap>>;
  /** Map of seasonal task id → "Jan 5, 2026" style date label. */
  lastDone: LastDoneMap;
  setLastDone: Dispatch<SetStateAction<LastDoneMap>>;
  hairLastDone: string | null;
  setHairLastDone: Dispatch<SetStateAction<string | null>>;
}

/**
 * Subscribes to the signed-in user's Firestore docs and exposes them as
 * React state. Each setter writes optimistically locally, then to Firestore.
 *
 * Daily checkmarks live at /users/{uid}/daily/{YYYY-MM-DD} so they "reset"
 * automatically as the calendar day rolls over.
 */
export function useChecklistState(): ChecklistState {
  const { state: authState } = useAuth();
  const uid = authState.status === "signedIn" ? authState.user.uid : null;

  // Locally-mirrored state.
  const [dailyChecked, setDailyChecked] = useState<CheckedMap>({});
  const [monthly, setMonthly] = useState<CheckedMap>({});
  const [sick, setSick] = useState<CheckedMap>({});
  const [seasonal, setSeasonal] = useState<LastDoneMap>({});
  const [hairLastDone, setHairLocal] = useState<string | null>(null);

  // True until the first snapshot for each subscription has arrived.
  const [loadCounts, setLoadCounts] = useState({ daily: 0, monthly: 0, sick: 0, seasonal: 0, hair: 0 });
  const loading =
    !uid ||
    loadCounts.daily === 0 ||
    loadCounts.monthly === 0 ||
    loadCounts.sick === 0 ||
    loadCounts.seasonal === 0 ||
    loadCounts.hair === 0;

  // The date string the daily subscription is bound to. Recompute on mount
  // and once a minute so the app rolls over when the user leaves it open.
  const [todayIso, setTodayIso] = useState(todayLocalIso());
  useEffect(() => {
    const id = setInterval(() => {
      const next = todayLocalIso();
      setTodayIso((cur) => (cur === next ? cur : next));
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Subscribe to all five docs whenever uid (or today's date) changes.
  useEffect(() => {
    if (!uid) return;

    const unsubDaily = onSnapshot(doc(db, "users", uid, "daily", todayIso), (snap) => {
      const data = snap.data() as { checked?: CheckedMap } | undefined;
      setDailyChecked(data?.checked ?? {});
      setLoadCounts((c) => ({ ...c, daily: c.daily + 1 }));
    });
    const unsubMonthly = onSnapshot(doc(db, "users", uid, "state", "monthly"), (snap) => {
      const data = snap.data() as { checked?: CheckedMap } | undefined;
      setMonthly(data?.checked ?? {});
      setLoadCounts((c) => ({ ...c, monthly: c.monthly + 1 }));
    });
    const unsubSick = onSnapshot(doc(db, "users", uid, "state", "sickday"), (snap) => {
      const data = snap.data() as { checked?: CheckedMap } | undefined;
      setSick(data?.checked ?? {});
      setLoadCounts((c) => ({ ...c, sick: c.sick + 1 }));
    });
    const unsubSeasonal = onSnapshot(doc(db, "users", uid, "state", "seasonal"), (snap) => {
      const data = snap.data() as { lastDone?: LastDoneMap } | undefined;
      setSeasonal(data?.lastDone ?? {});
      setLoadCounts((c) => ({ ...c, seasonal: c.seasonal + 1 }));
    });
    const unsubHair = onSnapshot(doc(db, "users", uid, "state", "hairwash"), (snap) => {
      const data = snap.data() as { lastDone?: string | null } | undefined;
      setHairLocal(data?.lastDone ?? null);
      setLoadCounts((c) => ({ ...c, hair: c.hair + 1 }));
    });

    return () => {
      unsubDaily();
      unsubMonthly();
      unsubSick();
      unsubSeasonal();
      unsubHair();
    };
  }, [uid, todayIso]);

  // Keep a ref to the latest values so setters can read fresh state without
  // depending on it in their useCallback deps (avoids re-creating setters
  // on every change, which would invalidate inputs / effects downstream).
  const ref = useRef({ dailyChecked, monthly, sick, seasonal, hairLastDone });
  ref.current = { dailyChecked, monthly, sick, seasonal, hairLastDone };

  const setChecked = useCallback<Dispatch<SetStateAction<CheckedMap>>>(
    (next) => {
      if (!uid) return;
      const newVal = resolveSetter(ref.current.dailyChecked, next);
      setDailyChecked(newVal); // optimistic
      void setDoc(doc(db, "users", uid, "daily", todayIso), { checked: newVal });
    },
    [uid, todayIso],
  );

  const setCheckedMonthly = useCallback<Dispatch<SetStateAction<CheckedMap>>>(
    (next) => {
      if (!uid) return;
      const newVal = resolveSetter(ref.current.monthly, next);
      setMonthly(newVal);
      void setDoc(doc(db, "users", uid, "state", "monthly"), { checked: newVal });
    },
    [uid],
  );

  const setCheckedSick = useCallback<Dispatch<SetStateAction<CheckedMap>>>(
    (next) => {
      if (!uid) return;
      const newVal = resolveSetter(ref.current.sick, next);
      setSick(newVal);
      void setDoc(doc(db, "users", uid, "state", "sickday"), { checked: newVal });
    },
    [uid],
  );

  const setLastDone = useCallback<Dispatch<SetStateAction<LastDoneMap>>>(
    (next) => {
      if (!uid) return;
      const newVal = resolveSetter(ref.current.seasonal, next);
      setSeasonal(newVal);
      void setDoc(doc(db, "users", uid, "state", "seasonal"), { lastDone: newVal });
    },
    [uid],
  );

  const setHairLastDone = useCallback<Dispatch<SetStateAction<string | null>>>(
    (next) => {
      if (!uid) return;
      const newVal = resolveSetter(ref.current.hairLastDone, next);
      setHairLocal(newVal);
      void setDoc(doc(db, "users", uid, "state", "hairwash"), { lastDone: newVal });
    },
    [uid],
  );

  return {
    loading,
    checked: dailyChecked,
    setChecked,
    checkedMonthly: monthly,
    setCheckedMonthly,
    checkedSick: sick,
    setCheckedSick,
    lastDone: seasonal,
    setLastDone,
    hairLastDone,
    setHairLastDone,
  };
}
